import type { CallSessionId, WebSocketMessage } from '@shared/types';
import { t } from '../i18n';
import { wsClient } from '../services/websocket';
import { webrtcManager } from '../services/webrtc';
import { callTabCoordination } from '../services/callTabCoordination';
import { useCallStore } from '../store/callStore';
import { useVaultStore } from '../store/vaultStore';
import { showToast } from '../utils/toast';
import { isWebViewMode } from '../utils/webviewBridge';
import { serverKeyFromServerUrl } from '../utils/serverKey';
import type { CallEndInfo } from './callTypes';
import type { IncomingCallUiEffects } from './callListenerEffects';
import { showSecurityToast } from './callListenerEffects';
import {
  forgetCancelledOutgoingCallSession,
  shouldCancelOutgoingCallSession,
  shouldIgnoreIncomingCallSession
} from './callSessionEndGuards';
import { addVerifiedIncomingCallHistory, deriveSafeIdentityId, getPinnedPublicKeyForIdentity, isSignalingKeyConsistentWithPinnedKey, markIncomingRejected, markOutgoingAnsweredVerified, showSoftKeyMismatchToast, updateHistoryRemoteIdentityId, updateHistoryRemoteIdentityName } from './callSecurityEffects';

type PendingCallAction = {
  callSessionId: string;
  action: 'answer' | 'reject';
  serverId?: string;
  at: number;
};

type CreateCallWsHandlersParams = {
  handleCallEnd: (info?: CallEndInfo) => void;
  scheduleIncomingCallStaleTimeout: (callSessionId: string) => void;
  scheduleAutoIceRecovery: (callSessionId: string, source: string) => void;
  incomingCallUiEffects: IncomingCallUiEffects;
  readPendingCallAction: () => PendingCallAction | null;
  clearPendingCallAction: () => void;
  stopRingtone: () => void;
  stopOutgoingDialTone: () => void;
  shouldIgnoreCallEndedSession: (callSessionId: string) => boolean;
};

export function createCallWsHandlers(params: CreateCallWsHandlersParams) {
  let callNotFoundHandledForSession: string | null = null;

  const handleIncomingCall = async (message: WebSocketMessage) => {
    const { callSessionId, fromIdentityPublicKey, offer, fromIdentityName, isTemporaryLinkCall } = message.data;
    const temporaryLinkCall = isTemporaryLinkCall === true;
    const currentCallState = useCallStore.getState();

    if (callSessionId && shouldIgnoreIncomingCallSession(String(callSessionId))) {
      wsClient.endCall(callSessionId);
      return;
    }

    const hasOtherActiveCall = !!currentCallState.activeCall
      && currentCallState.activeCall.callSessionId !== callSessionId;
    const hasOtherIncomingCall = !!currentCallState.incomingCall
      && currentCallState.incomingCall.callSessionId !== callSessionId;

    if (hasOtherActiveCall || hasOtherIncomingCall) {
      wsClient.endCall(callSessionId);
      return;
    }

    if (currentCallState.incomingCall?.callSessionId === callSessionId) {
      return;
    }

    const contacts = Object.values(useVaultStore.getState().vaultData?.contacts || {});
    const safeCallerIdentityId = await deriveSafeIdentityId(fromIdentityPublicKey);

    if (!fromIdentityPublicKey?.value || !safeCallerIdentityId) {
      console.error('[IncomingCallDiag] REJECT:missing_public_key', {
        callSessionId,
        hasPublicKeyValue: !!fromIdentityPublicKey?.value,
        hasSafeCallerIdentityId: !!safeCallerIdentityId
      });
      showSecurityToast('call.security.incomingMissingPublicKey');
      wsClient.endCall(callSessionId);
      return;
    }

    const contact = contacts.find((c) =>
      c.identities.some((id) => {
        const routedId = (id.identityId || '').trim();

        if (safeCallerIdentityId && routedId && routedId === safeCallerIdentityId) return true;
        if (fromIdentityPublicKey?.value && id.identityPublicKey?.value) {
          return id.identityPublicKey.value === fromIdentityPublicKey.value;
        }
        return false;
      })
    );
    const serverIdentityName = (fromIdentityName || '').trim();
    const fromDisplayName = contact?.displayName || serverIdentityName || t('common.unknown');
    const isKnownContact = !!contact;

    const callerPublicKeyBase64 = fromIdentityPublicKey.value;
    const callerIdentity = contact?.identities.find(
      (id) => id.identityId === safeCallerIdentityId
    );

    if (!isSignalingKeyConsistentWithPinnedKey({
      signalingKey: fromIdentityPublicKey,
      pinnedKey: callerIdentity?.identityPublicKey || null
    })) {
      console.error('[IncomingCallDiag] REJECT:key_mismatch', {
        callSessionId,
        safeCallerIdentityId,
        hasContact: !!contact,
        hasPinnedKey: !!callerIdentity?.identityPublicKey
      });
      showSoftKeyMismatchToast();
      wsClient.endCall(callSessionId);
      return;
    }

    if (!offer.dtlsSignature) {
      console.error('[IncomingCallDiag] REJECT:missing_dtls_signature', {
        callSessionId
      });
      showSecurityToast('call.security.incomingMissingSignature');
      wsClient.endCall(callSessionId);
      return;
    }

    const isValid = await webrtcManager.verifyRemoteDTLS(
      offer.sdp || '',
      offer.dtlsSignature,
      safeCallerIdentityId,
      callerPublicKeyBase64
    );

    if (!isValid) {
      console.error('[IncomingCallDiag] REJECT:dtls_verification_failed', {
        callSessionId,
        safeCallerIdentityId
      });
      showSecurityToast('call.security.incomingVerificationFailed');
      wsClient.endCall(callSessionId);
      return;
    }

    const connectionContext = wsClient.getConnectionContext();
    const localServerId = connectionContext?.serverUrl
      ? serverKeyFromServerUrl(connectionContext.serverUrl) || connectionContext.serverOrigin
      : connectionContext?.serverOrigin;

    useCallStore.getState().dispatchCallState({
      type: 'call/incoming-received',
      call: {
        callSessionId,
        fromIdentityId: safeCallerIdentityId,
        localIdentityId: connectionContext?.identityId,
        localServerId,
        localServerUrl: connectionContext?.serverUrl,
        isTemporaryLinkCall: temporaryLinkCall,
        fromDisplayName,
        fromIdentityName: serverIdentityName || undefined,
        isKnownContact,
        fromIdentityPublicKey: fromIdentityPublicKey || (callerIdentity?.identityPublicKey || undefined),
        e2eeVerified: true,
        offer
      }
    });
    addVerifiedIncomingCallHistory({
      callSessionId: String(callSessionId),
      remoteIdentityId: safeCallerIdentityId,
      remoteDisplayName: fromDisplayName,
      remoteIdentityName: serverIdentityName || undefined,
      remotePublicKey: fromIdentityPublicKey,
      isKnownContact,
      isTemporaryLinkCall: temporaryLinkCall,
      localIdentityId: connectionContext?.identityId,
      localServerId,
      localServerUrl: connectionContext?.serverUrl,
    });

    if (!isWebViewMode) {
      callTabCoordination.startManagingIncomingCall(String(callSessionId));
      params.scheduleIncomingCallStaleTimeout(String(callSessionId));
      params.incomingCallUiEffects.applyIncomingLeadershipState(String(callSessionId));
    }

    const pending = params.readPendingCallAction();
    if (pending && pending.callSessionId === callSessionId) {
      if (pending.action === 'reject') {
        markIncomingRejected(callSessionId);
        wsClient.endCall(callSessionId);
        useCallStore.getState().dispatchCallState({
          type: 'set-incoming-call',
          call: null
        });
        params.stopRingtone();
        params.clearPendingCallAction();
      } else if (pending.action === 'answer') {
        window.dispatchEvent(
          new CustomEvent('videocall:callAction', {
            detail: pending
          })
        );
      }
    }
  };

  const handleCallAnswered = async (message: WebSocketMessage) => {
    const { callSessionId, answer, fromIdentityPublicKey, fromIdentityName } = message.data;

    if (callSessionId && shouldCancelOutgoingCallSession(String(callSessionId))) {
      wsClient.endCall(callSessionId);
      params.handleCallEnd({ reason: 'local_cancelled_before_answer', source: 'user' });
      return;
    }

    const activeCall = useCallStore.getState().activeCall;
    if (!activeCall) {
      return;
    }

    if (callSessionId && activeCall.callSessionId !== callSessionId) {
      return;
    }

    forgetCancelledOutgoingCallSession(activeCall.callSessionId);
    params.stopOutgoingDialTone();
    useCallStore.getState().dispatchCallState({
      type: 'call/active-updated',
      patch: {
        connectionPhase: 'answer_received'
      }
    });

    const resolvedIdentityName = (fromIdentityName || '').trim();
    if (resolvedIdentityName && !activeCall.remoteIdentityName) {
      useCallStore.getState().dispatchCallState({
        type: 'call/active-updated',
        patch: {
          remoteIdentityName: resolvedIdentityName
        }
      });
      updateHistoryRemoteIdentityName(activeCall.callSessionId, resolvedIdentityName);
    }

    const safeCalleeIdentityId = await deriveSafeIdentityId(fromIdentityPublicKey);
    const effectiveCalleeIdentityId = safeCalleeIdentityId;

    if (!fromIdentityPublicKey?.value || !effectiveCalleeIdentityId) {
      console.error('❌ Call answer cannot be verified (missing/invalid callee public key) - ending call');
      showSecurityToast('call.security.answerMissingPublicKey');
      params.handleCallEnd({ reason: 'answer_missing_public_key', source: 'security' });
      return;
    }

    const pinnedCalleeKey = activeCall.remoteIdentityPublicKey || getPinnedPublicKeyForIdentity(effectiveCalleeIdentityId);
    if (!isSignalingKeyConsistentWithPinnedKey({
      signalingKey: fromIdentityPublicKey,
      pinnedKey: pinnedCalleeKey
    })) {
      console.error('❌ Call answer signaling key does not match pinned key - ending call');
      showSoftKeyMismatchToast();
      params.handleCallEnd({ reason: 'answer_key_mismatch', source: 'security' });
      return;
    }

    if (safeCalleeIdentityId && safeCalleeIdentityId !== activeCall.remoteIdentityId) {
      useCallStore.getState().dispatchCallState({
        type: 'call/active-updated',
        patch: {
          remoteIdentityId: safeCalleeIdentityId,
          remoteIdentityPublicKey: fromIdentityPublicKey
        }
      });
      updateHistoryRemoteIdentityId(activeCall.callSessionId, safeCalleeIdentityId);
    } else if (fromIdentityPublicKey?.value) {
      useCallStore.getState().dispatchCallState({
        type: 'call/active-updated',
        patch: {
          remoteIdentityPublicKey: fromIdentityPublicKey
        }
      });
    }

    if (!answer.dtlsSignature) {
      console.error('❌ Call answer missing E2EE signature - ending call');
      showSecurityToast('call.security.answerMissingSignature');
      params.handleCallEnd({ reason: 'answer_missing_signature', source: 'security' });
      return;
    }

    useCallStore.getState().dispatchCallState({
      type: 'call/active-updated',
      patch: {
        connectionPhase: 'verifying'
      }
    });

    const isValid = await webrtcManager.verifyRemoteDTLS(
      answer.sdp || '',
      answer.dtlsSignature,
      effectiveCalleeIdentityId,
      fromIdentityPublicKey.value
    );

    if (!isValid) {
      console.error('❌ DTLS signature verification FAILED - ending call');
      showSecurityToast('call.security.answerVerificationFailed');
      params.handleCallEnd({ reason: 'answer_verification_failed', source: 'security' });
      return;
    }

    markOutgoingAnsweredVerified({
      callSessionId: activeCall.callSessionId,
      remotePublicKey: fromIdentityPublicKey || activeCall.remoteIdentityPublicKey,
      remoteIdentityName: resolvedIdentityName || activeCall.remoteIdentityName
    });

    if (!activeCall.startedAt) {
      useCallStore.getState().dispatchCallState({
        type: 'call/active-updated',
        patch: {
          startedAt: new Date()
        }
      });
    }

    try {
      useCallStore.getState().dispatchCallState({
        type: 'call/active-updated',
        patch: {
          connectionPhase: 'negotiating'
        }
      });
      await webrtcManager.handleAnswer(answer);
      const latestActiveCall = useCallStore.getState().activeCall;
      if (latestActiveCall && latestActiveCall.status !== 'active') {
        useCallStore.getState().dispatchCallState({
          type: 'update-call-status',
          status: 'connecting'
        });
      }
      params.scheduleAutoIceRecovery(String(activeCall.callSessionId), 'handleCallAnswered');
    } catch (error) {
      console.error('Error handling answer:', error);
      params.handleCallEnd();
    }
  };

  const handleRenegotiateOffer = async (message: WebSocketMessage) => {
    const { callSessionId, offer, fromIdentityPublicKey, fromIdentityName } = message.data;
    const activeCall = useCallStore.getState().activeCall;
    if (!activeCall || activeCall.callSessionId !== callSessionId) return;

    const pk = fromIdentityPublicKey || activeCall.remoteIdentityPublicKey;
    if (!pk?.value || !offer?.sdp) {
      console.error('❌ Renegotiation offer missing public key or SDP - ending call');
      params.handleCallEnd();
      return;
    }

    const safeRemoteIdentityId = await deriveSafeIdentityId(pk);
    if (!safeRemoteIdentityId || safeRemoteIdentityId !== activeCall.remoteIdentityId) {
      console.error('❌ Renegotiation offer identity mismatch - ending call');
      params.handleCallEnd();
      return;
    }

    const pinnedRemoteKey = activeCall.remoteIdentityPublicKey || getPinnedPublicKeyForIdentity(safeRemoteIdentityId);
    if (!isSignalingKeyConsistentWithPinnedKey({
      signalingKey: pk,
      pinnedKey: pinnedRemoteKey
    })) {
      console.error('❌ Renegotiation offer signaling key does not match pinned key - ending call');
      showSoftKeyMismatchToast();
      params.handleCallEnd({ reason: 'renegotiate_offer_key_mismatch', source: 'security' });
      return;
    }

    const isValid = await webrtcManager.verifyRemoteDTLS(
      offer.sdp || '',
      offer.dtlsSignature,
      safeRemoteIdentityId,
      pk.value
    );

    if (!isValid) {
      showSecurityToast('call.security.answerVerificationFailed');
      params.handleCallEnd({ reason: 'renegotiate_offer_verification_failed', source: 'security' });
      return;
    }

    if (fromIdentityName && !activeCall.remoteIdentityName) {
      useCallStore.getState().dispatchCallState({
        type: 'call/active-updated',
        patch: {
          remoteIdentityName: String(fromIdentityName),
          remoteIdentityPublicKey: pk
        }
      });
    } else {
      useCallStore.getState().dispatchCallState({
        type: 'call/active-updated',
        patch: {
          remoteIdentityPublicKey: pk
        }
      });
    }

    try {
      await webrtcManager.handleRenegotiationOffer(callSessionId as CallSessionId, offer);
    } catch (error) {
      console.error('Error handling renegotiation offer:', error);
      params.handleCallEnd();
    }
  };

  const handleRenegotiateAnswer = async (message: WebSocketMessage) => {
    const { callSessionId, answer, fromIdentityPublicKey, fromIdentityName } = message.data;
    const activeCall = useCallStore.getState().activeCall;
    if (!activeCall || activeCall.callSessionId !== callSessionId) return;

    const pk = fromIdentityPublicKey || activeCall.remoteIdentityPublicKey;
    if (!pk?.value || !answer?.sdp) {
      console.error('❌ Renegotiation answer missing public key or SDP - ending call');
      params.handleCallEnd();
      return;
    }

    const pinnedRemoteKey = activeCall.remoteIdentityPublicKey || getPinnedPublicKeyForIdentity(activeCall.remoteIdentityId);
    if (!isSignalingKeyConsistentWithPinnedKey({
      signalingKey: pk,
      pinnedKey: pinnedRemoteKey
    })) {
      console.error('❌ Renegotiation answer signaling key does not match pinned key - ending call');
      showSoftKeyMismatchToast();
      params.handleCallEnd({ reason: 'renegotiate_answer_key_mismatch', source: 'security' });
      return;
    }

    const isValid = await webrtcManager.verifyRemoteDTLS(
      answer.sdp || '',
      answer.dtlsSignature,
      activeCall.remoteIdentityId,
      pk.value
    );
    if (!isValid) {
      showSecurityToast('call.security.answerVerificationFailed');
      params.handleCallEnd({ reason: 'renegotiate_answer_verification_failed', source: 'security' });
      return;
    }

    if (fromIdentityName && !activeCall.remoteIdentityName) {
      useCallStore.getState().dispatchCallState({
        type: 'call/active-updated',
        patch: {
          remoteIdentityName: String(fromIdentityName),
          remoteIdentityPublicKey: pk
        }
      });
    } else {
      useCallStore.getState().dispatchCallState({
        type: 'call/active-updated',
        patch: {
          remoteIdentityPublicKey: pk
        }
      });
    }

    try {
      await webrtcManager.handleRenegotiationAnswer(callSessionId as CallSessionId, answer);
    } catch (error) {
      console.error('Error handling renegotiation answer:', error);
      params.handleCallEnd();
    }
  };

  const handleIceCandidate = async (message: WebSocketMessage) => {
    const { callSessionId, candidate } = message.data;

    try {
      await webrtcManager.addIceCandidate(callSessionId as CallSessionId, candidate);
    } catch (error) {
      console.error('Error adding ICE candidate:', error);
    }
  };

  const handleCallEnded = async (message: WebSocketMessage) => {
    const { callSessionId, reason } = message.data || {};

    if (callSessionId && params.shouldIgnoreCallEndedSession(String(callSessionId))) {
      return;
    }

    const activeCall = useCallStore.getState().activeCall;
    const activeNativeCall = isWebViewMode ? activeCall : null;
    if (
      callSessionId
      && activeNativeCall?.callSessionId === callSessionId
      && reason === 'answered_elsewhere'
    ) {
      return;
    }

    const incomingCall = useCallStore.getState().incomingCall;
    const activeCallSessionId = activeCall?.callSessionId || null;
    const incomingCallSessionId = incomingCall?.callSessionId || null;
    if (
      callSessionId
      && callSessionId !== activeCallSessionId
      && callSessionId !== incomingCallSessionId
    ) {
      return;
    }

    if (callSessionId) {
      callTabCoordination.noteCallAction(String(callSessionId), 'ended');
    }
    params.handleCallEnd({ reason: (reason || 'remote_end').toString(), source: 'ws' });
  };

  const handleWebSocketError = (message: WebSocketMessage) => {
    const data = (message.data || {}) as { code?: string };
    if (data.code === 'DEVICE_NOT_FOUND' || data.code === 'IDENTITY_NOT_FOUND') {
      const state = useCallStore.getState();
      if (state.activeCall || state.incomingCall) {
        showToast({
          message: t('servers.serverAccessRemovedAction'),
          variant: 'warning',
          durationMs: 9000
        });
        params.handleCallEnd({ reason: 'circle_access_removed', source: 'ws' });
      }
      return;
    }
    if (data.code === 'RATE_LIMITED') {
      showToast({
        message: t('call.errors.rateLimited'),
        variant: 'warning',
        durationMs: 7000
      });
      params.handleCallEnd({ reason: 'rate_limited', source: 'ws' });
      return;
    }
    if (data.code !== 'CALL_NOT_FOUND') return;

    const state = useCallStore.getState();
    if (state.activeCall?.status === 'handoff') {
      return;
    }
    const callSessionId = state.activeCall?.callSessionId || state.incomingCall?.callSessionId || null;
    if (!callSessionId) return;
    if (callNotFoundHandledForSession === callSessionId) return;
    callNotFoundHandledForSession = callSessionId;

    params.handleCallEnd({ reason: 'call_not_found', source: 'ws' });
  };

  return {
    handleIncomingCall,
    handleCallAnswered,
    handleRenegotiateOffer,
    handleRenegotiateAnswer,
    handleIceCandidate,
    handleCallEnded,
    handleWebSocketError
  };
}
